import { useRef, useState, useContext } from 'react';
import { useNavigate } from "react-router";
import UserService from "../../Services/UserService";
import AuthenticationService from "../../Services/AuthenticationService";
import AuthContext from '../AuthContextComponent/AuthContext';
import Navigation from "../NavigationComponent/Navigation";
import TopNavigation from "../NavigationComponent/TopNavigation";
import classes from "./ChangePassword.module.css";
import "bootstrap/dist/css/bootstrap.min.css";
import LoadingIndicator from "../CommonComponent/LoadingIndicator";
import { ReactComponent as Error } from '../../Assets/img/Error.svg';
import { ReactComponent as Save } from '../../Assets/img/Edit-icon.svg';
import { ReactComponent as ResetIcon } from '../../Assets/img/Reset-primary-icon.svg';
import TopBackground from '../../Assets/img/LoginTopBackground.png';
import BottomBackground from '../../Assets/img/LoginBottomBacckground.png';
import ProductLogo from '../../Assets/img/ProductLogo.png';

/**
 * Renders the change password screen.
 *
 * Two modes:
 * - forced (props.forced): the user's password was set by an administrator and must be replaced
 *   before anything else. Rendered standalone by App.js for every route, deliberately without
 *   navigation so there is nothing to navigate away with. On success the user signs in again.
 * - normal: reached from the sidebar profile menu by any signed-in user, wrapped in the usual
 *   app chrome.
 *
 * The password always belongs to the signed-in user -- the API takes the username from the auth
 * token, so there is no username field.
 *
 * @component
 * @param {boolean} props.forced Renders the forced first-login variant
 */
function ChangePasswordPage(props) {

    //#region States

    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    //#endregion

    //#region Refs

    const currentPasswordInputRef = useRef();
    const newPasswordInputRef = useRef();
    const confirmPasswordInputRef = useRef();

    //#endregion

    const authCtx = useContext(AuthContext);
    let navigate = useNavigate();

    //#region Change password

    /**
    * Calls user service to change the signed-in user's password
    * @param {Event} event User action (on submitting form)
    */
    function changePasswordHandler(event) {
        event.preventDefault();

        const enteredCurrentPassword = currentPasswordInputRef.current.value;
        const enteredNewPassword = newPasswordInputRef.current.value;
        const enteredConfirmPassword = confirmPasswordInputRef.current.value;

        if (enteredNewPassword !== enteredConfirmPassword) {
            setError("New Password and Confirm Password not matching");
            return;
        }

        setError("");
        setIsLoading(true);

        var changePasswordDTO = {
            Request: {
                CurrentPassword: enteredCurrentPassword,
                NewPassword: enteredNewPassword,
                ConfirmPassword: enteredConfirmPassword,
            }
        };

        UserService.changePassword(changePasswordDTO)
            .then((response) => {
                setIsLoading(false);
                if (response.response == null) {
                    setError(response.errors?.[0]?.message || "Failed to change the password.");
                } else {
                    // The password these credentials were issued against no longer exists, so the
                    // session ends here and the user signs in again with the new one.
                    returnToLogin("Password changed successfully. Please sign in with your new password.");
                }
            })
            .catch((err) => {
                setIsLoading(false);
                setError(err.response?.data?.errors?.[0]?.message || "Failed to change the password.");
            });
    }

    //#endregion

    /**
    * Ends the session and returns to the login screen. Used after a successful change (the old
    * credentials are gone, so the session must not survive) and by the Back button (to abandon a
    * forced change -- a session that still owes a password change cannot do anything else anyway).
    * @param {string} [message] Confirmation to show on the login screen; omitted for Back.
    */
    function returnToLogin(message) {
        authCtx.logout();
        AuthenticationService.logout()
            .then(() => { })
            .catch(() => { });
        navigate("/login", { replace: true, state: message ? { message } : undefined });
    }

    if (isLoading) {
        return (
            <div className={classes.pageBody}>
                <LoadingIndicator size="large" message="Saving..." />
            </div>
        );
    }

    const body = (
        <>
            <span className={classes.heading}>Change Password</span>

            {props.forced &&
                <p className={classes.subtext}>
                    Your password was set by an administrator. Choose your own password to continue.
                </p>}

            <form className={classes.form} onSubmit={changePasswordHandler}>
                {error && (
                    <div className="alert alert-warning"><Error /><span className="message-popup-title">{error}</span></div>
                )}
                <div className={classes.field}>
                    <label className={`label ${classes.required}`}>Current Password</label>
                    <input className="InputField" type="password" ref={currentPasswordInputRef} autoComplete="current-password" required />
                </div>
                {/* new-password keeps the browser from filling these with the saved one */}
                <div className={classes.field}>
                    <label className={`label ${classes.required}`}>New Password</label>
                    {/* Mirrors the Identity password policy configured in Startup.cs
                        (services.Configure<IdentityOptions>). Keep the two in sync. */}
                    <ul className={classes.hintList}>
                        <li>At least 6 characters</li>
                        <li>One uppercase and one lowercase letter</li>
                        <li>One number</li>
                        <li>One special character (e.g. ! @ # $)</li>
                    </ul>
                    <input className="InputField" type="password" ref={newPasswordInputRef} autoComplete="new-password" required />
                </div>
                <div className={classes.field}>
                    <label className={`label ${classes.required}`}>Confirm New Password</label>
                    <input className="InputField" type="password" ref={confirmPasswordInputRef} autoComplete="new-password" required />
                </div>
                <div className={classes.actions}>
                    <button type="reset" className="secondary-button" onClick={() => setError("")}>
                        <span className="secondary-button-icon"><ResetIcon /></span>
                        <span className="secondary-button-text">Reset</span>
                    </button>
                    <button type="submit" className="primary-button">
                        <span className="primary-button-icon"><Save /></span>
                        <span className="primary-button-text">Change Password</span>
                    </button>
                </div>

                {/* Leaving the gate necessarily ends the session -- this account can't do
                    anything else until its password is replaced. No message: nothing changed. */}
                {props.forced &&
                    <div className={classes.backRow}>
                        <button type="button" className={classes.backLink} onClick={() => returnToLogin()}>
                            &#8592; Back to Login
                        </button>
                    </div>}
            </form>
        </>
    );

    // Forced mode is intentionally chrome-less -- no sidebar means no way into the rest of the app.
    // It reuses the Login screen's framing so arriving here straight from login feels continuous.
    if (props.forced) {
        return (
            <div className="outercontainer">
                <img src={TopBackground} className="topbackground" alt="" />
                <img src={ProductLogo} className="productlogo" alt="IDM System" />
                <div className={classes.forcedCard}>
                    {body}
                </div>
                <img src={BottomBackground} className="bottombackground" alt="" />
            </div>
        );
    }

    return (
        <>
            <Navigation />
            <TopNavigation />
            <div className="page-container">
                <div className={classes.pageBody}>
                    <div className={classes.card}>
                        {body}
                    </div>
                </div>
            </div>
        </>
    );
}

export default ChangePasswordPage;
